import React, { useRef } from 'react';
import { useAutoFocus } from '../hooks/useAutoFocus';
import { FormCellTW } from 'tailwindComponents/formCellTW/FormCellTW';
import classNames from 'classnames';
import { mergeTests } from '../../ui-toolkit/test-utilities/mergeTests';

export interface TextAreaTWProps {
  /** The value attribute of input. */
  value?: string;
  /** Text that will appear in an empty input. */
  placeholder?: string;
  /** Additional Tailwind utility classname */
  className?: string;
  /** Message to display when the input is in an error state. When this is present, also visually highlights the component as in error. */
  errorText?: string;
  /** Hint text content. */
  hintText?: string;
  /** Highlights the input as a required field (does also perform additional validation). */
  isRequired?: boolean;
  /** This label appears above the input. */
  labelText?: string;
  /** Specifies that the text input should automatically get focus. */
  autoFocus?: boolean;
  /** all elements are disabled*/
  disabled?: boolean;
  /** A callback triggered when component is focused. */
  // onFocus?: (event: any) => void;
  onFocus?: React.FocusEventHandler;
  /** A callback triggered when focus is removed. */
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  /** A callback fired when the value prop changes. */
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  /** A name for the component for Formik*/
  name?: string;
  maxLength?: number;
  rows?: number;
}

export const TextAreaTW: React.FC<TextAreaTWProps> = ({
  autoFocus,
  name,
  disabled,
  labelText,
  errorText,
  hintText,
  onFocus,
  isRequired,
  className,
  placeholder,
  onBlur,
  onChange,
  value,
  maxLength,
  rows,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutoFocus(!!autoFocus, textAreaRef);
  return (
    <FormCellTW
      disabled={disabled}
      labelText={labelText}
      errorText={errorText}
      isRequired={isRequired}
      hintText={hintText}
      name={name}
    >
      <textarea
        disabled={disabled}
        id={name}
        name={name}
        className={classNames(
          'block w-full rounded-md border-0 px-1.5 py-1.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
          {
            'text-red-900 placeholder:text-red-300 focus:outline-red-600 focus:ring-red-600': !!errorText,
            'text-gray-900 placeholder:text-gray-300 focus:ring-foundation-primary': !errorText,
          },
          'resize',
          className
        )}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={textAreaRef}
        data-test-id={mergeTests('textarea-tailwind')}
        maxLength={maxLength}
        rows={rows}
      />
    </FormCellTW>
  );
};
